import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, ChevronDown, ChevronUp, CircleHelp, ClipboardList, History, ListOrdered, Megaphone, MessageSquare, Settings, Star, User, X } from "lucide-react";
import { Dialog } from "@/components/modals";
import CountedAttemptHint from "@/components/courses/CountedAttemptHint";
import ClassAttendance from "@/components/teach/ClassAttendance";
import ClassGradebook from "@/components/teach/ClassGradebook";
import CourseAnnounceDialog, { type AnnounceTarget } from "@/components/courses/CourseAnnounceDialog";
import ResponseReview from "@/components/courses/ResponseReview";
import WorkThread, {
  WorkThreadTrigger,
  messageSubmissionId,
  type WorkThreadData,
} from "@/components/courses/WorkThread";
import StudentBodyParts from "@/components/student/StudentBodyParts";
import StudentKeyTerms from "@/components/student/StudentKeyTerms";
import StudentSlideDecks from "@/components/student/StudentSlideDecks";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import { api, errorMessage, post } from "@/lib/api";
import { gradeText } from "@/lib/gradeLabel";
import {
  partHasContent,
  partsFromBody,
  typeLocksOnDue,
  type BodyPart,
} from "@/lib/activityAnatomy";
import { questionTypeCaption, scoringBadge, type CorrectKey, type Scoring } from "@/lib/questionScoring";
import { inferSlideMode, normalizeSlideDecks } from "@/lib/slideDecks";
import useMediaQuery from "@/hooks/useMediaQuery";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./Teach.css";
import "@/styles/student.css";

const GRADE_COMPACT_QUERY = "(max-width: 68rem)";

type InstructorPerms = {
  proposeActivities: boolean;
  editActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
  studentProgressChat?: boolean;
};

type LearnerRow = {
  enrollmentId: string;
  uisUserId: string;
  uisUgpId: string;
  person: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  grade?: { percent: number | null; letter: string | null };
};

type WorkRow = {
  progressId: string;
  activityId: string;
  activityTitle: string | null;
  activityType?: string;
  learnerUgpId: string;
  progressStatus?: "in_progress" | "completed";
  hasQuestions?: boolean;
  attemptsUsed: number;
  maxAttempts: number | null;
  overrideAttempts: number | null;
  activityMaxAttempts: number | null;
  overrideStudentMayMessage?: boolean | null;
  overrideAllowLate?: boolean;
  dueAt?: string | null;
  activityStudentMayMessage?: boolean;
  isDraft: boolean;
  hasDraft: boolean;
  draftSubmissionId: string | null;
  gradingStatus: string | null;
  pendingCount: number;
  needsMark?: boolean;
  hasNewerPending?: boolean;
  newerTryAhead?: boolean;
  pointsEarned: number | null;
  pointsPossible: number | null;
  pointsPending: number | null;
  submittedAt: string | null;
  lastAt?: string | null;
  submissionId: string | null;
  teacherComment?: string | null;
};

type ClassDetail = {
  id: string;
  title: string;
  description?: string | null;
  schoolId: string;
  schoolName?: string | null;
  learnerCount: number;
  writeMode: "edit" | "propose" | "none";
  perms: InstructorPerms;
  canOpenOutline: boolean;
  canViewWork: boolean;
  canGradeWork: boolean;
  canOverrideWork: boolean;
  pendingCount: number | null;
  learners: LearnerRow[];
  work: WorkRow[];
  submissions?: WorkRow[];
};

type MarkResponse = {
  responseId: string;
  questionId: string;
  prompt: string | null;
  type: string;
  answer: unknown;
  options: unknown;
  correct: CorrectKey | null;
  scoring: (Partial<Scoring> & {
    presentation?: string;
    awardAttemptPoints?: boolean;
    attemptPoints?: number;
  }) | null;
  isCorrect: boolean | null;
  pointsPossible: number | null;
  pointsEarned: number | null;
  teacherComment: string | null;
  gradedAt: string | null;
  awaitingMarking: boolean;
};

type AttemptRow = {
  submissionId: string;
  attempt: number;
  submittedAt: string | null;
  gradingStatus: string;
  isPrimary: boolean;
  pointsEarned: number | null;
  pointsPossible: number | null;
  pointsPending: number | null;
};

type MarkingPaper = {
  id: string;
  activityId: string;
  activityTitle: string | null;
  courseId: string;
  courseTitle: string | null;
  learnerUgpId: string;
  progressId: string | null;
  attempt: number;
  submittedAt: string | null;
  gradingStatus: string;
  isPrimary: boolean;
  pointsEarned: number | null;
  pointsPossible: number | null;
  pointsPending: number | null;
  minutesEarned: number | null;
  gradePointsEarned?: number | null;
  gradePointsPossible?: number | null;
  gradeTeacherComment?: string | null;
  pointsAdjusted?: boolean;
  lessonChanged?: boolean;
  hasQuestions?: boolean;
  body?: BodyPart[] | null;
  content?: Record<string, unknown> | null;
  metadata?: { hasQuestions?: unknown; includes?: unknown } | null;
  estimatedMinutes?: number;
  timeLimitMinutes?: number;
  teacherComment?: string | null;
  activityType?: string | null;
  attemptsUsed?: number;
  maxAttempts?: number | null;
  overrideAttempts?: number | null;
  activityMaxAttempts?: number | null;
  overrideStudentMayMessage?: boolean | null;
  overrideAllowLate?: boolean;
  dueAt?: string | null;
  activityStudentMayMessage?: boolean;
  attempts: AttemptRow[];
  responses: MarkResponse[];
};

type OverrideTarget = Pick<
  WorkRow,
  | "progressId"
  | "activityId"
  | "activityTitle"
  | "activityType"
  | "learnerUgpId"
  | "attemptsUsed"
  | "maxAttempts"
  | "overrideAttempts"
  | "activityMaxAttempts"
  | "overrideStudentMayMessage"
  | "overrideAllowLate"
  | "dueAt"
  | "activityStudentMayMessage"
>;

type OverridePatch = {
  progressId: string;
  activityId: string;
  learnerUgpId: string;
  overrideAttempts: number | null;
  overrideStudentMayMessage: boolean | null;
  overrideAllowLate: boolean;
  activityMaxAttempts: number | null;
  maxAttempts: number | null;
};

type DiscussionGrade = {
  progressId?: string;
  submissionId?: string;
  learnerUgpId: string;
  gradingStatus: string;
  pointsEarned: number | null;
  pointsPossible: number | null;
  teacherComment: string | null;
  gradedAt: string | null;
};

type DiscussionPost = {
  id: string;
  body: string;
  authorName: string;
  authorUgpId?: string | null;
  at: string;
  grade?: DiscussionGrade | null;
  replies?: DiscussionPost[];
};

type DiscussionThread = {
  activityId: string;
  courseId: string;
  title: string | null;
  pointsPossible?: number | null;
  posts: DiscussionPost[];
};

function postAnchorId(id: string) {
  return `al-dpost-${id}`;
}

function postsByAuthor(posts: DiscussionPost[], ugp: string) {
  const ids: string[] = [];
  const walk = (rows: DiscussionPost[]) => {
    for (const row of rows) {
      if (ugp && row.authorUgpId === ugp) ids.push(row.id);
      if (row.replies?.length) walk(row.replies);
    }
  };
  walk(posts);
  return ids;
}

function scrollToDiscussionPost(id: string) {
  document.getElementById(postAnchorId(id))?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function DiscussionHitsNav({
  index,
  total,
  name,
  dock = false,
  onPrev,
  onNext,
}: {
  index: number;
  total: number;
  name: string;
  dock?: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div
      className={`al-dpost-jumps${dock ? " al-dpost-jumps--dock" : ""}`}
      role="navigation"
      aria-label={`${name}'s posts`}
    >
      <button
        type="button"
        className="al-classes-help"
        aria-label="Previous post from this student"
        onClick={onPrev}
      >
        <ChevronUp size={16} aria-hidden="true" />
      </button>
      <span className="al-dpost-jumps-count">
        {index + 1} / {total}
      </span>
      <button
        type="button"
        className="al-classes-help"
        aria-label="Next post from this student"
        onClick={onNext}
      >
        <ChevronDown size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

function learnerName(row: LearnerRow) {
  const name = [row.person?.firstName, row.person?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (row.person?.email) return row.person.email;
  return "Student";
}

function learnerSortKey(row: LearnerRow) {
  return [
    (row.person?.lastName || "").toLowerCase(),
    (row.person?.firstName || "").toLowerCase(),
    learnerName(row).toLowerCase(),
  ].join("\t");
}

function learnerMatchesQuery(row: LearnerRow, query: string) {
  const hay = [learnerName(row), row.person?.email || ""].join(" ").toLowerCase();
  return hay.includes(query);
}

function learnerLabel(n: number) {
  return n === 1 ? "1 student" : `${n} students`;
}

function nameForUgp(detail: ClassDetail | null, ugpId: string) {
  const row = detail?.learners.find((person) => person.uisUgpId === ugpId);
  return row ? learnerName(row) : "Student";
}

function formatWhen(value: string | null) {
  if (!value) return "";
  const ms = new Date(value).getTime();
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatDueDay(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { dateStyle: "medium" });
}

function ActivityReading({
  type,
  activityId,
  body,
  content,
}: {
  type: string;
  activityId?: string;
  body?: BodyPart[] | null;
  content?: Record<string, unknown> | null;
}) {
  const parts = partsFromBody(body).filter(partHasContent);
  const terms = termsFromContent(content);
  const decks = normalizeSlideDecks(content?.decks);
  const slideMode = inferSlideMode(content?.slideMode, decks);
  if (!parts.length && !terms.length && !decks.length) return null;
  return (
    <div className="acuity-student acuity-student--preview">
      {parts.length ? (
        <div className="student-reading">
          <StudentBodyParts parts={parts} activityId={activityId} />
        </div>
      ) : null}
      {decks.length ? (
        <StudentSlideDecks decks={decks} mode={slideMode} heading={type !== "slides"} />
      ) : null}
      {terms.length ? <StudentKeyTerms terms={terms} heading={type !== "key_terms"} /> : null}
    </div>
  );
}

function activityHasReading(body?: BodyPart[] | null, content?: Record<string, unknown> | null) {
  if (partsFromBody(body).some(partHasContent)) return true;
  if (termsFromContent(content).length) return true;
  if (normalizeSlideDecks(content?.decks).length) return true;
  return false;
}

function versionNote() {
  return "Current reading. The earlier reading was not saved.";
}

function attemptReceiptLine(paper: {
  attempt: number;
  isPrimary: boolean;
  pointsAdjusted?: boolean;
  pointsEarned: number | null;
  pointsPossible: number | null;
}) {
  const score =
    paper.pointsPossible == null
      ? null
      : `${paper.pointsEarned == null ? "—" : paper.pointsEarned} / ${paper.pointsPossible}`;
  const received = score ? `Received ${score}.` : "No points on this paper.";
  const marks = "Change it by marking the questions.";
  if (!paper.isPrimary) {
    return `${received} Not recommended to change this. It is not the attempt that counts, so the grade above stays.`;
  }
  if (paper.pointsAdjusted) return `${received} ${marks} Saving them replaces the points above.`;
  return `${received} ${marks} Saving them updates the points above.`;
}

function ActivityFold({
  openByDefault,
  activityId,
  children,
}: {
  openByDefault: boolean;
  activityId?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(openByDefault);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(openByDefault);
  }, [openByDefault, activityId]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > 196);
  }, [open, activityId, children]);

  return (
    <section className={`al-activity-fold${open ? " is-open" : ""}`} aria-label="Activity">
      <div className="al-activity-fold-frame">
        <div className="al-activity-fold-body" ref={bodyRef}>
          {children}
        </div>
        {open || !overflows ? null : <div className="al-activity-fold-fade" aria-hidden="true" />}
      </div>
      {overflows ? (
        <button
          type="button"
          className="al-activity-fold-toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? "Show less" : "Show the activity"}
          {open ? <ChevronUp size={15} aria-hidden="true" /> : <ChevronDown size={15} aria-hidden="true" />}
        </button>
      ) : null}
    </section>
  );
}

function termsFromContent(content: Record<string, unknown> | null | undefined) {
  const raw = content?.terms;
  if (!Array.isArray(raw)) return [];
  const out: { id: string; term: string; definition: string }[] = [];
  for (const [index, row] of raw.entries()) {
    if (!row || typeof row !== "object") continue;
    const rec = row as { id?: unknown; term?: unknown; definition?: unknown };
    const term = String(rec.term || "").trim();
    const definition = String(rec.definition || "").trim();
    if (!term && !definition) continue;
    out.push({
      id: String(rec.id || `term-${index}`),
      term,
      definition,
    });
  }
  return out;
}

function progressWorkCopy({
  hasQuestions,
  pointsPossible,
  canMessage,
}: {
  hasQuestions: boolean;
  pointsPossible: number | null;
  canMessage: boolean;
}) {
  if (hasQuestions) {
    return canMessage
      ? "This student has not handed in answers yet. You may still message them."
      : "This student has not handed in answers yet.";
  }
  if (pointsPossible) {
    return canMessage
      ? "This activity has no questions. You can still mark it, and you may message the student."
      : "This activity has no questions. You can still mark it.";
  }
  return canMessage
    ? "This activity did not have any questions or any points. You may still message the student."
    : "This activity did not have any questions or any points.";
}

function ThreadPost({
  post,
  nested = false,
  canGrade = false,
  defaultPossible = null,
  savingUgp = null,
  highlightUgp = null,
  currentPostId = null,
  onGrade,
}: {
  post: DiscussionPost;
  nested?: boolean;
  canGrade?: boolean;
  defaultPossible?: number | null;
  savingUgp?: string | null;
  highlightUgp?: string | null;
  currentPostId?: string | null;
  onGrade?: (learnerUgpId: string, points: string, comment: string) => void;
}) {
  const replies = post.replies || [];
  const focused = Boolean(highlightUgp && post.authorUgpId === highlightUgp);
  const current = focused && currentPostId === post.id;
  return (
    <article
      id={postAnchorId(post.id)}
      className={`al-dpost${nested ? " al-dpost--reply" : ""}`}
      data-author={post.authorUgpId || undefined}
    >
      <header className="al-dpost-head">
        <span className="al-dpost-who">
          <span className="al-dpost-name">{post.authorName}</span>
          {focused ? (
            <span
              className={`al-dpost-badge${current ? " is-current" : ""}`}
              aria-label="This student"
              title="This student"
            >
              <User size={11} aria-hidden="true" />
            </span>
          ) : null}
        </span>
        <time className="al-dpost-when" dateTime={post.at}>
          {formatWhen(post.at)}
        </time>
      </header>
      <p className="al-dpost-body">{post.body}</p>
      {!nested && post.authorUgpId && (canGrade || post.grade) ? (
        <PromptGrade
          post={post}
          defaultPossible={defaultPossible}
          canGrade={canGrade}
          saving={savingUgp === post.authorUgpId}
          onGrade={onGrade}
        />
      ) : null}
      {replies.length > 0 ? (
        <div className="al-dpost-replies" role="group" aria-label="Replies">
          {replies.map((reply) => (
            <ThreadPost
              key={reply.id}
              post={reply}
              nested
              highlightUgp={highlightUgp}
              currentPostId={currentPostId}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function PromptGrade({
  post,
  defaultPossible,
  canGrade,
  saving,
  onGrade,
}: {
  post: DiscussionPost;
  defaultPossible?: number | null;
  canGrade: boolean;
  saving: boolean;
  onGrade?: (learnerUgpId: string, points: string, comment: string) => void;
}) {
  const possible = post.grade?.pointsPossible ?? defaultPossible;
  const [points, setPoints] = useState(
    post.grade?.pointsEarned == null ? "" : String(post.grade.pointsEarned),
  );
  const [comment, setComment] = useState(post.grade?.teacherComment || "");
  useEffect(() => {
    setPoints(post.grade?.pointsEarned == null ? "" : String(post.grade.pointsEarned));
    setComment(post.grade?.teacherComment || "");
  }, [post.grade?.pointsEarned, post.grade?.teacherComment, post.grade?.gradedAt]);
  const pending = post.grade?.gradingStatus === "pending" || (!post.grade && possible);
  if (!possible) return null;
  return (
    <div className="al-dgrade">
      <div className="al-dgrade-row">
        <label className="al-dgrade-score">
          Grade
          <input
            type="number"
            min={0}
            max={possible}
            step="0.5"
            className="al-class-mark-input"
            value={points}
            disabled={!canGrade || saving}
            onChange={(e) => setPoints(e.target.value)}
          />
          <span>/ {possible}</span>
        </label>
        {pending && post.grade?.pointsEarned == null ? (
          <span className="al-class-chip al-class-chip--wait">Needs a mark</span>
        ) : post.grade?.pointsEarned != null ? (
          <span className="al-class-chip">
            {post.grade.pointsEarned} / {possible}
          </span>
        ) : null}
      </div>
      <textarea
        className="al-dgrade-comment"
        rows={2}
        placeholder="Comment for the student (optional)"
        value={comment}
        disabled={!canGrade || saving}
        onChange={(e) => setComment(e.target.value)}
      />
      {canGrade && post.authorUgpId && onGrade ? (
        <button
          type="button"
          className="al-grade-bulk-save"
          disabled={saving || points === ""}
          onClick={() => onGrade(post.authorUgpId!, points, comment)}
        >
          {saving ? "Saving…" : post.grade?.pointsEarned == null ? "Save mark" : "Update mark"}
        </button>
      ) : null}
    </div>
  );
}

function formatScore(row: { pointsEarned: number | null; pointsPossible: number | null; pointsPending: number | null }) {
  if (row.pointsPossible == null) return "No points";
  const earned = row.pointsEarned == null ? "—" : String(row.pointsEarned);
  const pending = row.pointsPending ? ` · ${row.pointsPending} unmarked` : "";
  return `${earned} / ${row.pointsPossible}${pending}`;
}

function GradeReceiptFields({
  earned,
  possible,
  minutes,
  minutesPossible,
  canEdit,
  saving,
  uncapped,
  onSave,
}: {
  earned: number | null;
  possible: number | null;
  minutes: number | null;
  minutesPossible?: number | null;
  canEdit: boolean;
  saving: boolean;
  uncapped?: boolean;
  onSave: (patch: { points?: string; minutes?: string }) => void;
}) {
  const savedPoints = earned == null ? "" : String(earned);
  const savedMinutes = minutes == null ? "" : String(minutes);
  const [points, setPoints] = useState(savedPoints);
  const [mins, setMins] = useState(savedMinutes);
  const canMark = canEdit && possible != null && possible > 0;

  useEffect(() => {
    setPoints(savedPoints);
  }, [savedPoints]);

  useEffect(() => {
    setMins(savedMinutes);
  }, [savedMinutes]);

  const pointsDirty = canMark && points.trim() !== "" && points.trim() !== savedPoints;
  const minutesDirty = canEdit && mins.trim() !== "" && mins.trim() !== savedMinutes;
  const dirty = pointsDirty || minutesDirty;

  const save = () => {
    if (!dirty || saving) return;
    onSave({
      points: pointsDirty ? points.trim() : undefined,
      minutes: minutesDirty ? mins.trim() : undefined,
    });
  };

  return (
    <>
      <div className="al-grade-fields">
        <label className="al-grade-field">
          <span className="al-grade-field-label">Points</span>
          <span className="al-grade-field-box">
            <input
              type="number"
              min={0}
              max={uncapped ? undefined : possible ?? undefined}
              step="0.5"
              className="al-class-mark-input"
              value={points}
              disabled={!canMark || saving}
              aria-label={possible != null ? `Points earned, out of ${possible}` : "Points earned"}
              onChange={(event) => setPoints(event.target.value)}
            />
            {possible != null ? <span className="al-grade-field-of">out of {possible}</span> : null}
          </span>
        </label>
        <label className="al-grade-field">
          <span className="al-grade-field-label">Minutes</span>
          <span className="al-grade-field-box">
            <input
              type="number"
              min={0}
              step="0.5"
              className="al-class-mark-input"
              value={mins}
              disabled={!canEdit || saving}
              aria-label={
                minutesPossible != null && minutesPossible > 0
                  ? `Primary minutes earned, out of ${minutesPossible}`
                  : "Primary minutes earned"
              }
              onChange={(event) => setMins(event.target.value)}
            />
            {minutesPossible != null && minutesPossible > 0 ? (
              <span className="al-grade-field-of">out of {minutesPossible}</span>
            ) : null}
          </span>
        </label>
      </div>
      {dirty
        ? createPortal(
            <button
              type="button"
              className="al-grade-float-save"
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving…" : "Save"}
            </button>,
            document.body,
          )
        : null}
    </>
  );
}

function formatAttemptsUsed(used: number, max: number | null) {
  if (max == null) return used === 1 ? "1 attempt used" : `${used} attempts used`;
  return `${used} of ${max} attempts used`;
}

function canStillRetry(used: number, max: number | null) {
  return max == null || used < max;
}

function nextAttemptCap(row: Pick<OverrideTarget, "attemptsUsed" | "maxAttempts">) {
  return (
    (row.maxAttempts == null ? row.attemptsUsed : Math.max(row.maxAttempts, row.attemptsUsed)) + 1
  );
}

function closeAttemptCap(used: number) {
  return Math.max(1, used);
}

function workRowFromPaper(paper: MarkingPaper): OverrideTarget {
  return {
    progressId: paper.progressId || "",
    activityId: paper.activityId,
    activityTitle: paper.activityTitle,
    activityType: paper.activityType || undefined,
    learnerUgpId: paper.learnerUgpId,
    attemptsUsed: paper.attemptsUsed ?? paper.attempts.length,
    maxAttempts: paper.maxAttempts ?? null,
    overrideAttempts: paper.overrideAttempts ?? null,
    activityMaxAttempts: paper.activityMaxAttempts ?? null,
    overrideStudentMayMessage: paper.overrideStudentMayMessage ?? null,
    overrideAllowLate: paper.overrideAllowLate === true,
    dueAt: paper.dueAt ?? null,
    activityStudentMayMessage: paper.activityStudentMayMessage ?? true,
  };
}

function resolveOverrideRow(
  overrideId: string | null,
  detail: ClassDetail | null,
  paper: MarkingPaper | null,
): OverrideTarget | null {
  if (!overrideId) return null;
  const fromList = classWork(detail).find((row) => row.progressId === overrideId) || null;
  if (fromList) return fromList;
  if (paper && (paper.progressId === overrideId || overrideId === "paper")) {
    return workRowFromPaper(paper);
  }
  return null;
}

function attemptCapToast(attempts: number, used: number) {
  if (attempts <= 0) return "Using the class limit again";
  if (attempts <= used) return "No more attempts for this student";
  return "Attempt limit updated";
}

function classWork(detail: ClassDetail | null): WorkRow[] {
  if (!detail) return [];
  return detail.work || detail.submissions || [];
}

type ProgressMark = {
  progressId: string;
  activityId: string;
  activityTitle: string | null;
  activityType: string | null;
  courseId: string;
  courseTitle: string | null;
  learnerUgpId: string;
  status: string;
  submissionId?: string | null;
  hasQuestions?: boolean;
  studentMayMessage?: boolean;
  dueAt?: string | null;
  estimatedMinutes?: number;
  timeLimitMinutes?: number;
  maxAttempts?: number | null;
  required?: boolean;
  body?: BodyPart[] | null;
  content?: Record<string, unknown> | null;
  metadata?: { hasQuestions?: unknown; includes?: unknown } | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  minutesEarned: number | null;
  lessonChanged?: boolean;
  teacherComment: string | null;
  gradedAt: string | null;
  completedAt: string | null;
  openedAt: string | null;
};

function isDraftRow(row: WorkRow) {
  return row.hasDraft === true && row.attemptsUsed === 0 && row.progressStatus !== "completed";
}

type MarkDraft = { points: string; comment: string };

/** Every answer worth points is editable, so every one of them carries a draft. */
function isScorable(row: MarkResponse) {
  return row.type !== "directions" && row.pointsPossible != null;
}

function savedMark(row: MarkResponse): MarkDraft {
  return {
    points: row.pointsEarned == null ? "" : String(row.pointsEarned),
    comment: row.teacherComment || "",
  };
}

function draftsFrom(responses: MarkResponse[]) {
  const out: Record<string, MarkDraft> = {};
  for (const row of responses) {
    if (isScorable(row)) out[row.responseId] = savedMark(row);
  }
  return out;
}

/** Whether this card holds an edit the server has not been told about yet. */
function markChanged(row: MarkResponse, draft: MarkDraft | undefined) {
  if (!draft) return false;
  const saved = savedMark(row);
  return draft.points.trim() !== saved.points || draft.comment.trim() !== saved.comment.trim();
}

/**
 * Right / wrong come from `isCorrect`. Attempt credit still awards points on a
 * miss, so reading the score alone would paint a wrong answer as a partial
 * mark. Unmarked stays unmarked. Manual (no flag) falls back to the points.
 */
function verdictFor(row: MarkResponse) {
  if (row.type === "directions") return { tone: "none", label: "Not scored" } as const;
  const worth = row.pointsPossible;
  if (worth == null) return { tone: "none", label: "No points" } as const;
  if (row.pointsEarned == null) return { tone: "wait", label: "Unmarked" } as const;
  const pts = row.pointsEarned;
  if (row.isCorrect === false) return { tone: "wrong", label: "Not correct" } as const;
  if (row.isCorrect === true) return { tone: "right", label: "Correct" } as const;
  if (pts >= worth) return { tone: "right", label: "Correct" } as const;
  if (pts <= 0) return { tone: "wrong", label: "Not correct" } as const;
  return { tone: "part", label: "Part" } as const;
}

function gradingKindBadges(row: MarkResponse) {
  if (row.type === "directions") return [];
  const badge = scoringBadge(row.type, row.scoring);
  const out: { key: string; label: string }[] = [];
  if (badge?.label === "Auto") out.push({ key: "auto", label: "Automatically Graded" });
  else if (badge?.label === "Free") out.push({ key: "free", label: "Free answer" });
  else if (badge?.label === "Manual") out.push({ key: "manual", label: "Manually graded" });
  const att = Number(row.scoring?.attemptPoints) || 0;
  if (row.scoring?.awardAttemptPoints === true && att > 0) {
    out.push({ key: "attempt", label: `Attempts get ${att} pt${att === 1 ? "" : "s"}` });
  }
  return out;
}

function scoreChipLabel(row: MarkResponse, draft: MarkDraft | undefined) {
  const score = outlineScore(row, draft);
  if (!score) return "";
  const unit = row.pointsPossible === 1 ? "point" : "points";
  return `${score} ${unit}`;
}

function outlineScore(row: MarkResponse, draft: MarkDraft | undefined) {
  const worth = row.pointsPossible;
  if (worth == null) return "";
  const raw = draft?.points.trim() ?? "";
  if (raw === "") return `?/${worth}`;
  return `${raw}/${worth}`;
}

function jumpToQuestion(responseId: string) {
  const node = document.getElementById(`al-grade-q-${responseId}`);
  node?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function promptText(value: string | null) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "Question";
}

function attemptsNewestFirst(rows: AttemptRow[]) {
  return [...rows].sort((a, b) => {
    const at = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
    const bt = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
    if (bt !== at) return bt - at;
    return (b.attempt || 0) - (a.attempt || 0);
  });
}

function OverrideTrigger({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`al-grade-dock-btn al-grade-dock-btn--icon${open ? " is-open" : ""}`}
      aria-expanded={open}
      aria-label="Special overrides"
      title="Special overrides"
      onClick={onClick}
    >
      <Settings size={16} aria-hidden="true" />
    </button>
  );
}

function PrimaryStar({ label = "The attempt that counts" }: { label?: string }) {
  return (
    <span className="al-grade-star-wrap" title={label}>
      <Star
        className="al-grade-star"
        size={14}
        strokeWidth={1.5}
        fill="currentColor"
        aria-label={label}
      />
    </span>
  );
}

function OverrideDialog({
  row,
  learner,
  grantingKey,
  onClose,
  onSetAttempts,
  onSetMessage,
  onSetLate,
}: {
  row: OverrideTarget | null;
  learner: string;
  grantingKey: string | null;
  onClose: () => void;
  onSetAttempts: (row: OverrideTarget, attempts: number) => void;
  onSetMessage: (row: OverrideTarget, studentMayMessage: boolean | null) => void;
  onSetLate: (row: OverrideTarget, allowLate: boolean) => void;
}) {
  const authoredMax = row?.activityMaxAttempts ?? null;
  const personalCap = row?.overrideAttempts ?? null;
  const canGrantAnother = Boolean(row && (row.maxAttempts != null || personalCap != null));
  const canCloseRetries = Boolean(row && row.attemptsUsed >= 1 && canStillRetry(row.attemptsUsed, row.maxAttempts));
  const savingAttempts = Boolean(row && grantingKey === (row.progressId || "paper"));
  const savingMessage = Boolean(row && grantingKey === `${row.progressId || "paper"}-message`);
  const savingLate = Boolean(row && grantingKey === `${row.progressId || "paper"}-late`);
  const dueLabel = row?.dueAt ? formatWhen(row.dueAt) : "";
  const duePassed = Boolean(row?.dueAt && new Date(row.dueAt).getTime() < Date.now());
  const locksOnDue = typeLocksOnDue(String(row?.activityType || ""));
  return (
    <Dialog
      open={Boolean(row)}
      title="Special overrides"
      subtitle={row ? `${learner} · ${row.activityTitle || "Activity"}` : undefined}
      width="26rem"
      onClose={() => {
        if (grantingKey) return;
        onClose();
      }}
    >
      {row ? (
        <div className="al-hint-copy">
          <p>
            Exceptions for this student on this activity only. The rest of the
            class still sits what you authored — one more try here is not one
            more try for everyone.
          </p>
          <section className="al-override-block">
            <h3>Attempts</h3>
            {!canGrantAnother && !canCloseRetries && authoredMax == null ? (
              <p>
                This activity already allows unlimited attempts, and they have
                not handed anything in yet, so there is nothing to raise or close.
              </p>
            ) : (
              <>
                <p>
                  They have used {row.attemptsUsed}
                  {row.maxAttempts == null
                    ? " (unlimited)."
                    : ` of ${row.maxAttempts}.`}
                  {authoredMax != null ? ` The activity itself allows ${authoredMax}.` : ""}
                  {personalCap != null
                    ? ` This student is capped at ${personalCap} until you change it — raising the activity later will not give them more.`
                    : canCloseRetries && canGrantAnother
                      ? " You can stop further attempts, or allow another try for this student only. That number stays put if you later change the activity."
                      : canGrantAnother
                        ? " Allow another try for this student only. That number stays put if you later change the activity."
                        : " Stop further attempts for this student only. That cap stays put if you later change the activity."}
                </p>
                <div className="al-override-actions">
                  {canCloseRetries ? (
                    <button
                      type="button"
                      className="al-classes-announce"
                      disabled={savingAttempts}
                      onClick={() => onSetAttempts(row, closeAttemptCap(row.attemptsUsed))}
                    >
                      No more attempts
                    </button>
                  ) : null}
                  {canGrantAnother ? (
                    <button
                      type="button"
                      className="al-classes-announce"
                      disabled={savingAttempts}
                      onClick={() => onSetAttempts(row, nextAttemptCap(row))}
                    >
                      Allow another
                    </button>
                  ) : null}
                  {personalCap != null ? (
                    <button
                      type="button"
                      className="al-class-work-undo"
                      disabled={savingAttempts}
                      onClick={() => onSetAttempts(row, 0)}
                    >
                      Use class limit
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </section>
          <section className="al-override-block">
            <h3>Due date</h3>
            <p>
              {!row.dueAt
                ? "This activity has no due date. Granting a makeup here only matters if you add one later."
                : !locksOnDue
                  ? `The class due date is ${dueLabel}. This kind of activity does not lock after that date.`
                  : `The class due date is ${dueLabel}.${duePassed ? " That date has passed." : ""}${
                      row.overrideAllowLate
                        ? " This student may still hand in after that."
                        : " They follow that date until you make an exception."
                    }`}
            </p>
            <div className="al-override-actions">
              {row.overrideAllowLate !== true ? (
                <button
                  type="button"
                  className="al-classes-announce"
                  disabled={savingLate}
                  onClick={() => onSetLate(row, true)}
                >
                  {savingLate ? "Saving…" : "Allow late"}
                </button>
              ) : (
                <button
                  type="button"
                  className="al-class-work-undo"
                  disabled={savingLate}
                  onClick={() => onSetLate(row, false)}
                >
                  {savingLate ? "Saving…" : "Use class due date"}
                </button>
              )}
            </div>
          </section>
          <section className="al-override-block">
            <h3>Work chat</h3>
            <p>
              {row.activityStudentMayMessage
                ? "The activity lets this student send messages on the public thread."
                : "The activity does not let students send messages on this item."}
              {row.overrideStudentMayMessage === false
                ? " This student is muted until you change it."
                : row.overrideStudentMayMessage === true
                  ? " This student may send messages even if the activity is muted."
                  : " They follow the activity until you set an exception."}
            </p>
            <div className="al-override-actions">
              {row.overrideStudentMayMessage !== false ? (
                <button
                  type="button"
                  className="al-classes-announce"
                  disabled={savingMessage}
                  onClick={() => onSetMessage(row, false)}
                >
                  {savingMessage ? "Saving…" : "Mute chat"}
                </button>
              ) : null}
              {row.overrideStudentMayMessage !== true && !row.activityStudentMayMessage ? (
                <button
                  type="button"
                  className="al-classes-announce"
                  disabled={savingMessage}
                  onClick={() => onSetMessage(row, true)}
                >
                  {savingMessage ? "Saving…" : "Allow messages"}
                </button>
              ) : null}
              {row.overrideStudentMayMessage != null ? (
                <button
                  type="button"
                  className="al-class-work-undo"
                  disabled={savingMessage}
                  onClick={() => onSetMessage(row, null)}
                >
                  Use activity setting
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}

function StudentPickerDialog({
  open,
  learners,
  selectedUgp,
  onClose,
  onPick,
}: {
  open: boolean;
  learners: LearnerRow[];
  selectedUgp: string | null;
  onClose: () => void;
  onPick: (ugpId: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);
  const needle = query.trim().toLowerCase();
  const sorted = [...learners].sort((a, b) => learnerSortKey(a).localeCompare(learnerSortKey(b)));
  const visible = needle ? sorted.filter((row) => learnerMatchesQuery(row, needle)) : sorted;
  return (
    <Dialog
      open={open}
      title="Student"
      subtitle="Filter the work list"
      width="22rem"
      maxHeight="28rem"
      onClose={onClose}
    >
      <input
        className="al-student-pick-search"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by name"
        aria-label="Search students"
        autoComplete="off"
      />
      <ul className="al-student-pick">
        {!needle ? (
          <li>
            <button
              type="button"
              className={`al-student-pick-btn${selectedUgp == null ? " is-on" : ""}`}
              aria-pressed={selectedUgp == null}
              onClick={() => onPick(null)}
            >
              Everyone
            </button>
          </li>
        ) : null}
        {visible.map((learner) => (
          <li key={learner.enrollmentId}>
            <button
              type="button"
              className={`al-student-pick-btn${selectedUgp === learner.uisUgpId ? " is-on" : ""}`}
              aria-pressed={selectedUgp === learner.uisUgpId}
              onClick={() => onPick(learner.uisUgpId)}
            >
              <span>{learnerName(learner)}</span>
              <span className="al-student-pick-meta">
                {learner.grade?.percent != null ? gradeText(learner.grade) : "No marks yet"}
                {learner.person?.email ? ` · ${learner.person.email}` : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {visible.length === 0 ? <p className="al-class-empty">No matching students.</p> : null}
    </Dialog>
  );
}

function CloseRetriesDialog({
  open,
  used,
  max,
  saving,
  onClose,
  onStop,
}: {
  open: boolean;
  used: number;
  max: number | null;
  saving: boolean;
  onClose: () => void;
  onStop: () => void;
}) {
  return (
    <Dialog
      open={open}
      title="Further attempts?"
      width="24rem"
      onClose={() => {
        if (saving) return;
        onClose();
      }}
      footer={
        <>
          <button type="button" className="al-class-work-undo" disabled={saving} onClick={onClose}>
            Keep allowing
          </button>
          <button type="button" className="al-classes-announce" disabled={saving} onClick={onStop}>
            {saving ? "Saving…" : "Stop attempts"}
          </button>
        </>
      }
    >
      <div className="al-hint-copy">
        <p>
          They have used {used}
          {max == null ? ", and this activity has no cap." : ` of ${max}.`}
          {" "}Stop further attempts for this student? If you skip this, you can still change it from the gear on Attempts — including allowing more tries.
        </p>
      </div>
    </Dialog>
  );
}

function QuestionJumpList({
  responses,
  marks,
  onJump,
}: {
  responses: MarkResponse[];
  marks?: Record<string, MarkDraft>;
  onJump: (responseId: string) => void;
}) {
  return (
    <ul className="al-grade-q-outline">
      {responses.map((row, index) => {
        const verdict = verdictFor(row);
        const score = outlineScore(row, marks?.[row.responseId]);
        return (
          <li key={row.responseId}>
            <button
              type="button"
              className={`al-grade-q-jump al-grade-q-jump--${verdict.tone}`}
              onClick={() => onJump(row.responseId)}
            >
              <span className="al-grade-q-n">{index + 1}</span>
              <span className={`al-grade-verdict al-grade-verdict--${verdict.tone}`}>
                {verdict.tone === "right" ? (
                  <Check size={12} strokeWidth={2.5} aria-hidden="true" />
                ) : verdict.tone === "wrong" ? (
                  <X size={12} strokeWidth={2.5} aria-hidden="true" />
                ) : null}
                {verdict.label}
              </span>
              {score ? <span className="al-grade-q-outline-score">{score}</span> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function ClassWorkspace() {
  const { courseId = "", submissionId = "", discussionId = "", progressId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusStudentUgp = searchParams.get("student") || "";
  const { capabilities, pageAccess, loading: authLoading } = useAuth();
  const { notice, toast } = useNotification();
  const canOpen = Boolean(
    pageAccess?.teach || capabilities?.teachEnrolled || capabilities?.teachWithoutEnrollment,
  );
  const canAnnounce = Boolean(capabilities?.postAnnouncements);
  const compactGrader = useMediaQuery(GRADE_COMPACT_QUERY);
  const [gradeDock, setGradeDock] = useState<null | "attempts" | "questions">(null);

  const [detail, setDetail] = useState<ClassDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [attemptsHint, setAttemptsHint] = useState(false);
  const [keyHint, setKeyHint] = useState(false);
  const [paper, setPaper] = useState<MarkingPaper | null>(null);
  const [paperLoading, setPaperLoading] = useState(false);
  const [pendingAttemptId, setPendingAttemptId] = useState<string | null>(null);
  const [paperMissing, setPaperMissing] = useState(false);
  const [marks, setMarks] = useState<Record<string, MarkDraft>>({});
  const [editingPts, setEditingPts] = useState<string | null>(null);
  const [commentingId, setCommentingId] = useState<string | null>(null);
  const [savingMarks, setSavingMarks] = useState(false);
  const [grantingKey, setGrantingKey] = useState<string | null>(null);
  const [overrideId, setOverrideId] = useState<string | null>(null);
  const [closeRetriesOpen, setCloseRetriesOpen] = useState(false);
  const [workFilter, setWorkFilter] = useState<"grade" | "path">("grade");
  const [classView, setClassView] = useState<"work" | "gradebook" | "attendance">("work");
  const [studentFilter, setStudentFilter] = useState<string | null>(null);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [focusHit, setFocusHit] = useState(0);
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadAudience, setThreadAudience] = useState("public");
  const [threadTab, setThreadTab] = useState("all");
  const [workThread, setWorkThread] = useState<WorkThreadData | null>(null);
  const [threadSending, setThreadSending] = useState(false);
  const [thread, setThread] = useState<DiscussionThread | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadMissing, setThreadMissing] = useState(false);
  const [gradingUgp, setGradingUgp] = useState<string | null>(null);
  const [progressMark, setProgressMark] = useState<ProgressMark | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressMissing, setProgressMissing] = useState(false);
  const [progressPts, setProgressPts] = useState("");
  const [progressComment, setProgressComment] = useState("");
  const [savingProgress, setSavingProgress] = useState(false);
  const [savingMinutes, setSavingMinutes] = useState(false);
  const [nonPrimaryCue, setNonPrimaryCue] = useState<{ id: string; tick: number } | null>(null);

  useEffect(() => {
    if (!nonPrimaryCue) return;
    const timer = window.setTimeout(() => setNonPrimaryCue(null), 3000);
    return () => window.clearTimeout(timer);
  }, [nonPrimaryCue]);

  const classPath = `/teach/${encodeURIComponent(courseId)}`;

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!courseId) return;
    if (!opts?.silent) setLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: ClassDetail }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}`,
      );
      setDetail(envelope.data);
      setMissing(false);
    } catch (err) {
      setDetail(null);
      setMissing(true);
      notice({
        type: "error",
        title: "Could not open this course",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [courseId, notice]);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void load();
  }, [authLoading, canOpen, load]);

  useEffect(() => {
    if (!detail || submissionId || location.hash !== "#work") return;
    const el = document.getElementById("work");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [detail, location.hash, submissionId]);

  /**
   * The paper follows the URL rather than a piece of state, so an attempt is
   * a link. A teacher who wants a second opinion can send it to a colleague,
   * and Back leaves the grader instead of leaving the class.
   */
  const loadPaper = useCallback(async () => {
    if (!submissionId) {
      setPaper(null);
      setPaperMissing(false);
      setPendingAttemptId(null);
      setPaperLoading(false);
      return;
    }
    setPaperLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: MarkingPaper }>(
        `/api/marking/submissions/${encodeURIComponent(submissionId)}`,
      );
      const next = envelope.data;
      setPaper(next);
      setPaperMissing(false);
      setMarks(draftsFrom(next.responses));
      setEditingPts(null);
      setCommentingId(null);
      if (next.id) setThreadTab(next.id);
    } catch (err) {
      setPaper(null);
      setPaperMissing(true);
      notice({
        type: "error",
        title: "Could not open that attempt",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setPaperLoading(false);
      setPendingAttemptId(null);
    }
  }, [notice, submissionId]);

  useLayoutEffect(() => {
    if (submissionId && paper?.id !== submissionId) {
      setPendingAttemptId(submissionId);
    }
  }, [submissionId, paper?.id]);

  const loadThread = useCallback(async () => {
    if (!discussionId || !courseId) {
      setThread(null);
      setThreadMissing(false);
      return;
    }
    setThreadLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: DiscussionThread }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/discussions/${encodeURIComponent(discussionId)}`,
      );
      setThread(envelope.data);
      setThreadMissing(false);
    } catch (err) {
      setThread(null);
      setThreadMissing(true);
      notice({
        type: "error",
        title: "Could not open that discussion",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setThreadLoading(false);
    }
  }, [courseId, discussionId, notice]);

  const saveDiscussionGrade = useCallback(
    async (learnerUgpId: string, points: string, comment: string) => {
      if (!courseId || !discussionId) return;
      setGradingUgp(learnerUgpId);
      try {
        const envelope = await post<{ success: boolean; data: DiscussionThread }>(
          `/api/teaching/classes/${encodeURIComponent(courseId)}/discussions/${encodeURIComponent(discussionId)}/grade`,
          { learnerUgpId, points, comment },
        );
        setThread(envelope.data);
        notice({ type: "success", title: "Mark saved", duration: 2500 });
      } catch (err) {
        notice({
          type: "error",
          title: "Could not save that mark",
          subtext: errorMessage(err),
          duration: 6000,
        });
      } finally {
        setGradingUgp(null);
      }
    },
    [courseId, discussionId, notice],
  );

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void loadPaper();
  }, [authLoading, canOpen, loadPaper]);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void loadThread();
  }, [authLoading, canOpen, loadThread]);

  useEffect(() => {
    if (!compactGrader) setGradeDock(null);
  }, [compactGrader]);

  useEffect(() => {
    if (!submissionId) setCloseRetriesOpen(false);
  }, [submissionId]);

  useEffect(() => {
    if (!commentingId) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [commentingId]);

  useEffect(() => {
    if (!commentingId && !gradeDock) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (commentingId) {
        setCommentingId(null);
        return;
      }
      setGradeDock(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [commentingId, gradeDock]);

  const editMark = (responseId: string, patch: Partial<MarkDraft>) => {
    setMarks((prev) => ({
      ...prev,
      [responseId]: {
        points: patch.points ?? prev[responseId]?.points ?? "",
        comment: patch.comment ?? prev[responseId]?.comment ?? "",
      },
    }));
  };

  /**
   * Save one card, or every card that has been edited.
   *
   * The endpoint takes a batch either way, so a single card is a batch of one
   * and the totals settle once instead of once per answer.
   */
  const saveMarks = async (ids: string[]) => {
    if (!submissionId || !paper) return;
    const wanted = new Set(ids);
    const payload = paper.responses
      .filter((row) => wanted.has(row.responseId) && isScorable(row))
      .map((row) => ({
        responseId: row.responseId,
        points: marks[row.responseId]?.points,
        comment: marks[row.responseId]?.comment,
      }))
      .filter((row) => String(row.points ?? "").trim() !== "");
    if (!payload.length) {
      notice({
        type: "warning",
        title: "Nothing to save",
        subtext: "Enter a score first. A blank score leaves the answer unmarked.",
        duration: 4000,
      });
      return;
    }
    setSavingMarks(true);
    const wasPending = paper.gradingStatus === "pending";
    try {
      const envelope = await post<{ success: boolean; data: MarkingPaper }>(
        `/api/marking/submissions/${encodeURIComponent(submissionId)}/marks`,
        { marks: payload },
      );
      const next = envelope.data;
      setPaper(next);
      setMarks(draftsFrom(next.responses));
      setEditingPts(null);
      setCommentingId(null);
      toast.success(payload.length === 1 ? "Mark saved" : `${payload.length} marks saved`);
      // Stay on the paper: the teacher usually wants to see what they just
      // awarded, and whether this attempt is now the one that counts.
      await Promise.all([load(), loadWorkThread()]);
      const used = next.attemptsUsed ?? next.attempts.length;
      if (
        wasPending &&
        next.gradingStatus !== "pending" &&
        detail?.canOverrideWork &&
        canStillRetry(used, next.maxAttempts ?? null)
      ) {
        setCloseRetriesOpen(true);
      }
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save marks",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSavingMarks(false);
    }
  };

  const loadProgress = useCallback(async () => {
    if (!courseId || !progressId) {
      setProgressMark(null);
      setProgressMissing(false);
      return;
    }
    setProgressLoading(true);
    setProgressMissing(false);
    try {
      const envelope = await api<{ success: boolean; data: ProgressMark }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/progress/${encodeURIComponent(progressId)}`,
      );
      setProgressMark(envelope.data);
      setProgressPts(envelope.data.pointsEarned == null ? "" : String(envelope.data.pointsEarned));
      setProgressComment(envelope.data.teacherComment || "");
    } catch {
      setProgressMark(null);
      setProgressMissing(true);
    } finally {
      setProgressLoading(false);
    }
  }, [courseId, progressId]);

  useEffect(() => {
    if (!progressId) {
      setProgressMark(null);
      setProgressMissing(false);
      return;
    }
    void loadProgress();
  }, [progressId, loadProgress]);

  const saveProgressGrade = async (points = progressPts, comment = progressComment) => {
    if (!courseId || !progressId || !progressMark) return;
    if (points.trim() === "") {
      notice({
        type: "warning",
        title: "Enter a score first",
        duration: 4000,
      });
      return;
    }
    setSavingProgress(true);
    try {
      const envelope = await post<{ success: boolean; data: ProgressMark }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/progress/${encodeURIComponent(progressId)}/grade`,
        { points, comment },
      );
      setProgressMark(envelope.data);
      setProgressPts(envelope.data.pointsEarned == null ? "" : String(envelope.data.pointsEarned));
      setProgressComment(envelope.data.teacherComment || "");
      notice({ type: "success", title: "Mark saved", duration: 2500 });
      await Promise.all([load(), loadWorkThread()]);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save that mark",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSavingProgress(false);
    }
  };

  const savePaperGrade = async (points: string, comment: string) => {
    if (!courseId || !paper?.progressId) return;
    if (points.trim() === "") return;
    setSavingProgress(true);
    try {
      await post(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/progress/${encodeURIComponent(paper.progressId)}/grade`,
        { points, comment },
      );
      notice({ type: "success", title: "Grade saved", duration: 2500 });
      await loadPaper();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save that grade",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSavingProgress(false);
    }
  };

  const saveSubmissionMinutes = async (minutes: string) => {
    if (!submissionId) return;
    setSavingMinutes(true);
    try {
      const envelope = await post<{ success: boolean; data: MarkingPaper }>(
        `/api/marking/submissions/${encodeURIComponent(submissionId)}/minutes`,
        { minutes },
      );
      setPaper(envelope.data);
      notice({ type: "success", title: "Minutes saved", duration: 2500 });
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save minutes",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSavingMinutes(false);
    }
  };

  const saveProgressMinutes = async (minutes: string) => {
    if (!courseId || !progressId) return;
    setSavingMinutes(true);
    try {
      const envelope = await post<{ success: boolean; data: ProgressMark }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/progress/${encodeURIComponent(progressId)}/minutes`,
        { minutes },
      );
      setProgressMark(envelope.data);
      notice({ type: "success", title: "Minutes saved", duration: 2500 });
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save minutes",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSavingMinutes(false);
    }
  };

  const saveProgressReceipt = async (patch: { points?: string; minutes?: string; comment?: string }) => {
    if (patch.points != null) await saveProgressGrade(patch.points, patch.comment ?? progressComment);
    if (patch.minutes != null) await saveProgressMinutes(patch.minutes);
  };

  const savePaperReceipt = async (patch: { points?: string; minutes?: string; comment?: string }) => {
    if (patch.points != null) await savePaperGrade(patch.points, patch.comment ?? paper?.gradeTeacherComment ?? "");
    if (patch.minutes != null) await saveSubmissionMinutes(patch.minutes);
  };

  const applyOverridePatch = (data: OverridePatch) => {
    setPaper((prev) => {
      if (!prev) return prev;
      if (prev.activityId !== data.activityId || prev.learnerUgpId !== data.learnerUgpId) return prev;
      return {
        ...prev,
        progressId: data.progressId || prev.progressId,
        maxAttempts: data.maxAttempts,
        overrideAttempts: data.overrideAttempts,
        activityMaxAttempts: data.activityMaxAttempts,
        overrideStudentMayMessage: data.overrideStudentMayMessage,
        overrideAllowLate: data.overrideAllowLate === true,
      };
    });
    setOverrideId((current) => {
      if (current === "paper" && data.progressId) return data.progressId;
      return current;
    });
  };

  const setAttemptCap = async (row: OverrideTarget, attempts: number) => {
    if (!courseId || !detail?.canOverrideWork) return false;
    const key = row.progressId || "paper";
    setGrantingKey(key);
    try {
      const envelope = await post<{ success: boolean; data: OverridePatch }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/overrides`,
        {
          activityId: row.activityId,
          learnerUgpId: row.learnerUgpId,
          attempts,
        },
      );
      applyOverridePatch(envelope.data);
      toast.success(attemptCapToast(attempts, row.attemptsUsed));
      await load({ silent: true });
      return true;
    } catch (err) {
      notice({
        type: "error",
        title: "Could not change attempts",
        subtext: errorMessage(err),
        duration: 6000,
      });
      return false;
    } finally {
      setGrantingKey(null);
    }
  };

  const setMessageOverride = async (row: OverrideTarget, studentMayMessage: boolean | null) => {
    if (!courseId || !detail?.canOverrideWork) return;
    const key = `${row.progressId || "paper"}-message`;
    setGrantingKey(key);
    try {
      const envelope = await post<{ success: boolean; data: OverridePatch }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/overrides`,
        {
          activityId: row.activityId,
          learnerUgpId: row.learnerUgpId,
          studentMayMessage,
        },
      );
      applyOverridePatch(envelope.data);
      toast.success(
        studentMayMessage == null
          ? "Using the activity chat setting again"
          : studentMayMessage
            ? "This student may send messages"
            : "Student messages muted for this student",
      );
      await load({ silent: true });
    } catch (err) {
      notice({
        type: "error",
        title: "Could not change chat",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setGrantingKey(null);
    }
  };

  const setLateOverride = async (row: OverrideTarget, allowLate: boolean) => {
    if (!courseId || !detail?.canOverrideWork) return;
    const key = `${row.progressId || "paper"}-late`;
    setGrantingKey(key);
    try {
      const envelope = await post<{ success: boolean; data: OverridePatch }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/overrides`,
        {
          activityId: row.activityId,
          learnerUgpId: row.learnerUgpId,
          allowLate,
        },
      );
      applyOverridePatch(envelope.data);
      toast.success(
        allowLate ? "This student may hand in after the due date" : "Using the class due date again",
      );
      await load({ silent: true });
    } catch (err) {
      notice({
        type: "error",
        title: "Could not change due date",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setGrantingKey(null);
    }
  };

  const loadWorkThread = useCallback(async () => {
    const threadProgressId =
      paper?.progressId ||
      progressMark?.progressId ||
      classWork(detail).find(
        (row) =>
          (paper && row.activityId === paper.activityId && row.learnerUgpId === paper.learnerUgpId) ||
          (progressMark &&
            row.activityId === progressMark.activityId &&
            row.learnerUgpId === progressMark.learnerUgpId) ||
          (discussionId &&
            focusStudentUgp &&
            row.activityId === discussionId &&
            row.learnerUgpId === focusStudentUgp),
      )?.progressId;
    if (!courseId || !threadProgressId) {
      setWorkThread(null);
      return;
    }
    try {
      const envelope = await api<{ success: boolean; data: WorkThreadData }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/work/${encodeURIComponent(threadProgressId)}/thread?audience=${encodeURIComponent(threadAudience)}`,
      );
      setWorkThread(envelope.data);
    } catch {
      setWorkThread(null);
    }
  }, [
    courseId,
    detail,
    discussionId,
    focusStudentUgp,
    paper?.activityId,
    paper?.learnerUgpId,
    paper?.progressId,
    progressMark,
    threadAudience,
  ]);

  useEffect(() => {
    if (!paper?.progressId && !progressMark?.progressId && !(discussionId && focusStudentUgp)) {
      setWorkThread(null);
      return;
    }
    void loadWorkThread();
  }, [
    discussionId,
    focusStudentUgp,
    loadWorkThread,
    paper?.progressId,
    progressMark?.progressId,
    threadAudience,
  ]);

  useEffect(() => {
    setThreadTab("all");
  }, [paper?.progressId, progressMark?.progressId]);

  useEffect(() => {
    const attempts = workThread?.attempts || [];
    if (threadTab !== "all" && !attempts.some((row) => row.submissionId === threadTab)) {
      setThreadTab("all");
    }
  }, [workThread, threadTab]);

  useEffect(() => {
    if (!discussionId || !focusStudentUgp || !thread) return;
    setFocusHit(0);
    const first = postsByAuthor(thread.posts, focusStudentUgp)[0];
    if (!first) return;
    const timer = window.setTimeout(() => scrollToDiscussionPost(first), 40);
    return () => window.clearTimeout(timer);
  }, [discussionId, focusStudentUgp, thread?.activityId]);

  const allWork = classWork(detail);
  const scopedWork = studentFilter
    ? allWork.filter((row) => row.learnerUgpId === studentFilter)
    : allWork;
  const toGradeWork = scopedWork.filter((row) => row.needsMark);
  const visibleWork =
    workFilter === "grade"
      ? toGradeWork
      : [...scopedWork].sort((a, b) => {
          const at = a.lastAt ? new Date(a.lastAt).getTime() : 0;
          const bt = b.lastAt ? new Date(b.lastAt).getTime() : 0;
          const am = Number.isFinite(at) ? at : 0;
          const bm = Number.isFinite(bt) ? bt : 0;
          if (bm !== am) return bm - am;
          return (b.attemptsUsed || 0) - (a.attemptsUsed || 0);
        });

  if (authLoading) {
    return <LoadingOverlay isVisible message="Loading…" />;
  }

  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("teach")} />;
  }

  if (discussionId) {
    const focusName = focusStudentUgp ? nameForUgp(detail, focusStudentUgp) : "";
    const discussionProgressId = classWork(detail).find(
      (row) => row.activityId === discussionId && row.learnerUgpId === focusStudentUgp,
    )?.progressId;
    const focusHits = thread && focusStudentUgp ? postsByAuthor(thread.posts, focusStudentUgp) : [];
    const currentPostId = focusHits[Math.min(focusHit, Math.max(0, focusHits.length - 1))] || null;
    const stepFocusHit = (delta: number) => {
      if (!focusHits.length) return;
      const next = (focusHit + delta + focusHits.length * 10) % focusHits.length;
      setFocusHit(next);
      scrollToDiscussionPost(focusHits[next]);
    };
    const hitsNav =
      focusHits.length > 1 ? (
        <DiscussionHitsNav
          index={Math.min(focusHit, focusHits.length - 1)}
          total={focusHits.length}
          name={focusName || "Student"}
          onPrev={() => stepFocusHit(-1)}
          onNext={() => stepFocusHit(1)}
        />
      ) : null;
    const sendDiscussionMessage = async (body: string) => {
      if (!courseId || !discussionProgressId) return;
      setThreadSending(true);
      try {
        const envelope = await post<{ success: boolean; data: WorkThreadData }>(
          `/api/teaching/classes/${encodeURIComponent(courseId)}/work/${encodeURIComponent(discussionProgressId)}/thread/messages`,
          {
            audience: threadAudience,
            submissionId: messageSubmissionId(threadTab, workThread?.attempts),
            body,
          },
        );
        setWorkThread(envelope.data);
      } catch (err) {
        notice({
          type: "error",
          title: "Could not send",
          subtext: errorMessage(err),
          duration: 5000,
        });
      } finally {
        setThreadSending(false);
      }
    };
    return (
      <div className="al-classes al-classes--wide">
        <LoadingOverlay isVisible={threadLoading && !thread} message="Loading…" />
        <Link to={classPath} className="al-class-back">
          <ArrowLeft size={14} aria-hidden="true" />
          {detail?.title || "Class"}
        </Link>
        {threadMissing && !threadLoading ? (
          <div className="al-classes-empty">
            <h2>Not available</h2>
            <p>This discussion is not one you can read.</p>
          </div>
        ) : null}
        {thread ? (
          <>
            <header className="al-grade-head">
              <div className="al-grade-head-main">
                <p className="al-grade-eyebrow">Discussion</p>
                <h1>{thread.title || "Discussion"}</h1>
                <p className="al-classes-sub">
                  {focusName ? `${focusName} · ` : ""}
                  {thread.posts.length === 1 ? "1 response" : `${thread.posts.length} responses`}
                  {thread.pointsPossible
                    ? ` · marked out of ${thread.pointsPossible}`
                    : ""}
                </p>
              </div>
              {focusStudentUgp && discussionProgressId ? (
                <div className="al-grade-head-score">
                  <div className="al-work-thread-head-btn">
                    <WorkThreadTrigger open={threadOpen} onClick={() => setThreadOpen((on) => !on)} />
                  </div>
                </div>
              ) : null}
            </header>
            {hitsNav}
            {thread.posts.length === 0 ? (
              <p className="al-class-empty">No one has posted yet.</p>
            ) : (
              <div className="al-dthread">
                {thread.posts.map((post) => (
                  <ThreadPost
                    key={post.id}
                    post={post}
                    canGrade={Boolean(detail?.canGradeWork)}
                    defaultPossible={thread.pointsPossible ?? null}
                    savingUgp={gradingUgp}
                    highlightUgp={focusStudentUgp || null}
                    currentPostId={currentPostId}
                    onGrade={(ugp, points, comment) => void saveDiscussionGrade(ugp, points, comment)}
                  />
                ))}
              </div>
            )}
            {compactGrader && (focusHits.length > 1 || (focusStudentUgp && discussionProgressId)) ? (
              <div className="al-grade-docks">
                {focusHits.length > 1 ? (
                  <DiscussionHitsNav
                    index={Math.min(focusHit, focusHits.length - 1)}
                    total={focusHits.length}
                    name={focusName || "Student"}
                    dock
                    onPrev={() => stepFocusHit(-1)}
                    onNext={() => stepFocusHit(1)}
                  />
                ) : null}
                {focusStudentUgp && discussionProgressId ? (
                  <WorkThreadTrigger
                    open={threadOpen}
                    onClick={() => setThreadOpen((on) => !on)}
                  />
                ) : null}
              </div>
            ) : null}
            {threadOpen && focusStudentUgp && discussionProgressId
              ? createPortal(
                  <div id="al-work-thread-panel" className="al-work-thread-panel" role="dialog" aria-label="Messages">
                    <div className="al-work-thread-panel-head">
                      <button
                        type="button"
                        className="al-grade-dock-close"
                        aria-label="Close messages"
                        onClick={() => setThreadOpen(false)}
                        style={{ display: "inline-flex", marginLeft: "auto" }}
                      >
                        <X size={15} aria-hidden="true" />
                      </button>
                    </div>
                    <WorkThread
                      data={workThread}
                      tab={threadTab}
                      audience={threadAudience}
                      channels
                      sending={threadSending}
                      onAudienceChange={(next) => setThreadAudience(next)}
                      onTabChange={setThreadTab}
                      onSend={sendDiscussionMessage}
                    />
                  </div>,
                  document.body,
                )
              : null}
          </>
        ) : null}
      </div>
    );
  }

  /**
   * The grader is a page, not a dialog. Marking is the longest single thing a
   * teacher does here — a written answer, the key beside it, the score, then
   * the next one — and none of that fits in a column you have to scroll past
   * the class behind it.
   *
   * The score lives on each answer rather than in a panel off to the side.
   * Marking is a per-question act: read this one, award this one, comment on
   * this one. A single column of totals beside nine questions only ever told
   * the teacher what the header already says.
   */
  if (progressId) {
    if (progressMark?.submissionId) {
      return (
        <Navigate
          to={`${classPath}/work/${encodeURIComponent(progressMark.submissionId)}`}
          replace
        />
      );
    }
    const canGrade = Boolean(detail?.canGradeWork);
    const possible = progressMark?.pointsPossible ?? null;
    const threadProgressId = progressMark?.progressId || progressId;
    const hasQuestions = Boolean(progressMark?.hasQuestions);
    const canMessage = workThread ? workThread.canReply : Boolean(detail?.canViewWork);
    const dueLabel = formatDueDay(progressMark?.dueAt);
    const sendProgressMessage = async (body: string) => {
      if (!courseId || !threadProgressId) return;
      setThreadSending(true);
      try {
        const envelope = await post<{ success: boolean; data: WorkThreadData }>(
          `/api/teaching/classes/${encodeURIComponent(courseId)}/work/${encodeURIComponent(threadProgressId)}/thread/messages`,
          {
            audience: threadAudience,
            submissionId: messageSubmissionId(threadTab, workThread?.attempts),
            body,
          },
        );
        setWorkThread(envelope.data);
      } catch (err) {
        notice({
          type: "error",
          title: "Could not send",
          subtext: errorMessage(err),
          duration: 5000,
        });
      } finally {
        setThreadSending(false);
      }
    };
    const progressThread = (
      <WorkThread
        data={workThread}
        tab={threadTab}
        audience={threadAudience}
        channels
        sending={threadSending}
        onAudienceChange={(next) => setThreadAudience(next)}
        onTabChange={setThreadTab}
        onSend={sendProgressMessage}
      />
    );
    return (
      <div className="al-classes al-classes--wide">
        <LoadingOverlay isVisible={progressLoading && !progressMark} message="Loading…" />
        <Link to={classPath} className="al-class-back">
          <ArrowLeft size={14} aria-hidden="true" />
          {detail?.title || "Class"}
        </Link>
        {progressMissing && !progressLoading ? (
          <div className="al-classes-empty">
            <h2>Not available</h2>
            <p>This work is not one you can read.</p>
          </div>
        ) : null}
        {progressMark ? (
          <>
            <header className="al-grade-head">
              <div className="al-grade-head-main">
                <p className="al-grade-eyebrow">
                  {nameForUgp(detail, progressMark.learnerUgpId)}
                </p>
                <h1>{progressMark.activityTitle || "Activity"}</h1>
                <p className="al-classes-sub">
                  {progressMark.status === "completed" ? "Completed" : "Opened"}
                  {progressMark.completedAt
                    ? ` · ${formatWhen(progressMark.completedAt)}`
                    : progressMark.openedAt
                      ? ` · ${formatWhen(progressMark.openedAt)}`
                      : ""}
                  {dueLabel ? ` · due ${dueLabel}` : ""}
                  {progressMark.required === false ? " · optional" : ""}
                </p>
              </div>
              <div className="al-grade-head-score">
                <span className="al-grade-chips">
                  {progressMark.status === "completed" ? (
                    <span className="al-class-chip">Completed</span>
                  ) : (
                    <span className="al-class-chip">Opened</span>
                  )}
                  {possible != null && progressMark.pointsEarned == null ? (
                    <span className="al-class-chip al-class-chip--wait">Needs a mark</span>
                  ) : null}
                </span>
                <div className="al-work-thread-head-btn">
                  <WorkThreadTrigger open={threadOpen} onClick={() => setThreadOpen((on) => !on)} />
                </div>
              </div>
            </header>
            <main className="al-grade-main">
              <p className="al-progress-note">
                {progressWorkCopy({ hasQuestions, pointsPossible: possible, canMessage })}
              </p>
              {progressMark.lessonChanged ? (
                <p className="al-grade-version al-grade-version--band">{versionNote()}</p>
              ) : null}
              <p className="al-grade-kicker">Grade</p>
              <GradeReceiptFields
                earned={progressMark.pointsEarned}
                possible={possible}
                minutes={progressMark.minutesEarned}
                minutesPossible={progressMark.estimatedMinutes || null}
                canEdit={canGrade}
                saving={savingProgress || savingMinutes}
                onSave={(patch) => void saveProgressReceipt(patch)}
              />
              {activityHasReading(progressMark.body, progressMark.content) ? (
                <ActivityFold openByDefault={!hasQuestions} activityId={progressMark.activityId}>
                  <ActivityReading
                    type={progressMark.activityType || "reading"}
                    activityId={progressMark.activityId}
                    body={progressMark.body}
                    content={progressMark.content}
                  />
                </ActivityFold>
              ) : null}
            </main>
            {compactGrader ? (
              <div className="al-grade-docks">
                <WorkThreadTrigger
                  open={threadOpen}
                  onClick={() => setThreadOpen((on) => !on)}
                />
              </div>
            ) : null}
            {threadOpen
              ? createPortal(
                  <div id="al-work-thread-panel" className="al-work-thread-panel" role="dialog" aria-label="Messages">
                    <div className="al-work-thread-panel-head">
                      <button
                        type="button"
                        className="al-grade-dock-close"
                        aria-label="Close messages"
                        onClick={() => setThreadOpen(false)}
                        style={{ display: "inline-flex", marginLeft: "auto" }}
                      >
                        <X size={15} aria-hidden="true" />
                      </button>
                    </div>
                    {progressThread}
                  </div>,
                  document.body,
                )
              : null}
          </>
        ) : null}
      </div>
    );
  }

  if (submissionId) {
    const canGrade = Boolean(detail?.canGradeWork);
    const awaiting = paper?.responses.filter((row) => row.awaitingMarking) || [];
    const dirtyIds = (paper?.responses || [])
      .filter((row) => isScorable(row) && markChanged(row, marks[row.responseId]))
      .map((row) => row.responseId);
    const targetAttemptId = pendingAttemptId || submissionId;
    const showPaperOverlay = Boolean(
      submissionId && (paperLoading || pendingAttemptId) && (!paper || paper.id !== targetAttemptId),
    );
    const railAttempts = paper ? attemptsNewestFirst(paper.attempts) : [];
    const commentingRow =
      paper?.responses.find((row) => row.responseId === commentingId) || null;
    const commentingIndex =
      commentingRow && paper
        ? paper.responses.findIndex((row) => row.responseId === commentingRow.responseId)
        : -1;
    const commentingDraft = commentingId ? marks[commentingId] : undefined;
    const commentingChanged = commentingRow
      ? markChanged(commentingRow, commentingDraft)
      : false;
    const overrideOpen = Boolean(overrideId);
    const openPaperOverride = () => {
      if (overrideOpen) {
        setOverrideId(null);
        return;
      }
      setCloseRetriesOpen(false);
      setThreadOpen(false);
      setGradeDock(null);
      setOverrideId(paper?.progressId || "paper");
    };

    return (
      <div className="al-classes al-classes--wide">
        <LoadingOverlay isVisible={showPaperOverlay} message="Loading attempt…" />
        <Link to={classPath} className="al-class-back">
          <ArrowLeft size={14} aria-hidden="true" />
          {detail?.title || "Class"}
        </Link>

        {paperMissing && !paperLoading ? (
          <div className="al-classes-empty">
            <h2>Not available</h2>
            <p>
              This attempt is not one you can read, or it has not been handed in
              yet.
            </p>
          </div>
        ) : null}

        {paper ? (
          <>
            <header className="al-grade-head">
              <div className="al-grade-head-main">
                <p className="al-grade-eyebrow">{paper.courseTitle || detail?.title || "Course"}</p>
                <div className="al-classes-title-row">
                  <h1>{paper.activityTitle || "Activity"}</h1>
                  <button
                    type="button"
                    className="al-classes-help"
                    aria-label="How marking works"
                    title="How marking works"
                    onClick={() => setKeyHint(true)}
                  >
                    <CircleHelp size={16} aria-hidden="true" />
                  </button>
                </div>
                <p className="al-classes-sub">
                  {nameForUgp(detail, paper.learnerUgpId)} · attempt {paper.attempt}
                  {paper.submittedAt ? ` · handed in ${formatWhen(paper.submittedAt)}` : ""}
                </p>
              </div>
              <div className="al-grade-head-score">
                <span className="al-grade-chips">
                  {paper.isPrimary ? (
                    <PrimaryStar />
                  ) : (
                    <span className="al-class-chip">Another try counts</span>
                  )}
                  {awaiting.length ? (
                    <span className="al-class-chip al-class-chip--wait">
                      {awaiting.length === 1 ? "1 unmarked" : `${awaiting.length} unmarked`}
                    </span>
                  ) : paper.gradingStatus === "pending" ? (
                    <span className="al-class-chip al-class-chip--wait">Needs a mark</span>
                  ) : null}
                </span>
                <div className="al-work-thread-head-btn">
                  <WorkThreadTrigger
                    open={threadOpen}
                    onClick={() => {
                      setThreadOpen((on) => !on);
                      setOverrideId(null);
                    }}
                  />
                  {detail?.canOverrideWork ? (
                    <OverrideTrigger open={overrideOpen} onClick={openPaperOverride} />
                  ) : null}
                </div>
              </div>
            </header>

            <div className="al-grade-body">
              {compactGrader && gradeDock ? (
                <button
                  type="button"
                  className="al-grade-scrim"
                  aria-label="Close panel"
                  onClick={() => setGradeDock(null)}
                />
              ) : null}
              <aside
                id="al-grade-rail"
                className={`al-grade-rail${gradeDock ? " is-open" : ""}`}
              >
                {!compactGrader || gradeDock === "attempts" ? (
                  <div className="al-grade-rail-block">
                    <div className="al-grade-rail-head">
                      <h2>Attempts</h2>
                      <div className="al-grade-rail-tools">
                        <button
                          type="button"
                          className="al-classes-help"
                          aria-label="Which attempt counts — the gold star"
                          title="Which attempt counts — the gold star"
                          onClick={() => setAttemptsHint(true)}
                        >
                          <CircleHelp size={15} aria-hidden="true" />
                        </button>
                        {compactGrader ? (
                          <button
                            type="button"
                            className="al-grade-dock-close"
                            aria-label="Close attempts"
                            onClick={() => setGradeDock(null)}
                          >
                            <X size={15} aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <p className="al-grade-attempts-cap">
                      {formatAttemptsUsed(
                        paper.attemptsUsed ?? railAttempts.length,
                        paper.maxAttempts ?? null,
                      )}
                      {paper.overrideAttempts != null ? " · personal limit" : ""}
                    </p>
                    <ul className="al-grade-attempts">
                      {railAttempts.map((row) => {
                        const active = row.submissionId === paper.id;
                        return (
                          <li key={row.submissionId}>
                            <Link
                              to={`${classPath}/work/${encodeURIComponent(row.submissionId)}`}
                              className={`al-grade-attempt${active ? " is-active" : ""}`}
                              aria-current={active ? "true" : undefined}
                              onClick={() => {
                                if (row.submissionId !== paper.id) {
                                  setPendingAttemptId(row.submissionId);
                                  setPaperLoading(true);
                                }
                              }}
                            >
                              <span className="al-grade-attempt-n">
                                Attempt {row.attempt}
                                {row.isPrimary ? <PrimaryStar /> : null}
                              </span>
                              {row.submittedAt ? (
                                <span className="al-grade-attempt-meta">{formatWhen(row.submittedAt)}</span>
                              ) : null}
                              <span className="al-grade-attempt-meta">{formatScore(row)}</span>
                              <span className="al-grade-chips">
                                {row.isPrimary && row.gradingStatus === "pending" ? (
                                  <span className="al-class-chip al-class-chip--wait">Needs a mark</span>
                                ) : null}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}

                {paper.responses.length > 0 && (!compactGrader || gradeDock === "questions") ? (
                  <div className="al-grade-rail-block">
                    <div className="al-grade-rail-head">
                      <h2>Questions</h2>
                      <div className="al-grade-rail-tools">
                        {compactGrader ? (
                          <button
                            type="button"
                            className="al-grade-dock-close"
                            aria-label="Close questions"
                            onClick={() => setGradeDock(null)}
                          >
                            <X size={15} aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <QuestionJumpList
                      responses={paper.responses}
                      marks={marks}
                      onJump={(id) => {
                        jumpToQuestion(id);
                        if (compactGrader) setGradeDock(null);
                      }}
                    />
                  </div>
                ) : null}
              </aside>

              <main className="al-grade-main">
                {paper.lessonChanged ? (
                  <p className="al-grade-version al-grade-version--band">{versionNote()}</p>
                ) : null}
                <p className="al-grade-kicker">Grade</p>
                <GradeReceiptFields
                  earned={paper.gradePointsEarned ?? paper.pointsEarned}
                  possible={paper.gradePointsPossible ?? paper.pointsPossible}
                  minutes={paper.minutesEarned}
                  minutesPossible={paper.estimatedMinutes || null}
                  canEdit={canGrade && Boolean(paper.progressId)}
                  saving={savingProgress || savingMinutes}
                  uncapped={Boolean(paper.hasQuestions)}
                  onSave={(patch) => void savePaperReceipt(patch)}
                />
                {activityHasReading(paper.body, paper.content) ? (
                  <ActivityFold
                    key={paper.id}
                    openByDefault={paper.responses.length === 0}
                    activityId={paper.activityId}
                  >
                    <ActivityReading
                      type={paper.activityType || "reading"}
                      activityId={paper.activityId}
                      body={paper.body}
                      content={paper.content}
                    />
                  </ActivityFold>
                ) : null}
                {paper.responses.length ? (
                  <section className="al-attempt" aria-label={`Attempt ${paper.attempt}`}>
                    <header className="al-attempt-head">
                      <h2>Attempt {paper.attempt}</h2>
                      <p>{attemptReceiptLine(paper)}</p>
                    </header>
                {paper.responses.map((row, index) => {
                  const worth = row.pointsPossible;
                  const verdict = verdictFor(row);
                  const draft = marks[row.responseId];
                  const changed = markChanged(row, draft);
                  return (
                    <article
                      key={row.responseId}
                      id={`al-grade-q-${row.responseId}`}
                      className={`al-grade-q al-grade-q--${verdict.tone}`}
                    >
                      <header className="al-grade-q-head">
                        <span className="al-grade-q-n">{index + 1}</span>
                        <div className="al-grade-q-head-text">
                          <div className="al-grade-q-topline">
                            <p className="al-class-q-prompt">{promptText(row.prompt)}</p>
                            <div className="al-grade-q-flags">
                              <span className={`al-grade-verdict al-grade-verdict--${verdict.tone}`}>
                                {verdict.tone === "right" ? (
                                  <Check size={13} strokeWidth={2.5} aria-hidden="true" />
                                ) : verdict.tone === "wrong" ? (
                                  <X size={13} strokeWidth={2.5} aria-hidden="true" />
                                ) : null}
                                {verdict.label}
                              </span>
                            </div>
                          </div>
                          <div className="al-grade-q-sub">
                            <p className="al-class-q-type">
                              {questionTypeCaption(row.type, row.scoring?.presentation)}
                            </p>
                            <div className="al-grade-q-scoreline">
                              {canGrade && isScorable(row) ? (
                                editingPts === row.responseId ? (
                                  <span className="al-grade-pts-edit">
                                    <input
                                      id={`al-pts-${row.responseId}`}
                                      type="number"
                                      min={0}
                                      max={worth ?? undefined}
                                      step={0.5}
                                      className="al-class-mark-input"
                                      value={draft?.points ?? ""}
                                      autoFocus
                                      onChange={(event) =>
                                        editMark(row.responseId, { points: event.target.value })
                                      }
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter" && changed) {
                                          event.preventDefault();
                                          void saveMarks([row.responseId]);
                                        }
                                        if (event.key === "Escape" && !changed) setEditingPts(null);
                                      }}
                                    />
                                    {worth != null ? <span>/ {worth}</span> : null}
                                    <button
                                      type="button"
                                      className="al-grade-pts-quick"
                                      onClick={() =>
                                        editMark(row.responseId, { points: String(worth ?? 0) })
                                      }
                                    >
                                      Full
                                    </button>
                                    <button
                                      type="button"
                                      className="al-grade-pts-quick"
                                      onClick={() => editMark(row.responseId, { points: "0" })}
                                    >
                                      Zero
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className={`al-grade-pts${
                                      verdict.tone === "right" ? " is-full" : ""
                                    }`}
                                    title="Change points"
                                    onClick={() => {
                                      setEditingPts(row.responseId);
                                      if (!paper.isPrimary) setNonPrimaryCue({ id: row.responseId, tick: Date.now() });
                                    }}
                                  >
                                    {scoreChipLabel(row, draft)}
                                  </button>
                                )
                              ) : isScorable(row) ? (
                                <span
                                  className={`al-grade-pts${verdict.tone === "right" ? " is-full" : ""}`}
                                >
                                  {scoreChipLabel(row, draft)}
                                </span>
                              ) : null}
                              {nonPrimaryCue?.id === row.responseId ? (
                                <p className="al-attempt-cue" role="status">
                                  Not recommended. This attempt is not the one that counts.
                                </p>
                              ) : null}
                              {canGrade && isScorable(row) ? (
                                <button
                                  type="button"
                                  className={`al-grade-icon-btn${
                                    commentingId === row.responseId || (draft?.comment || "").trim()
                                      ? " is-on"
                                      : ""
                                  }`}
                                  aria-expanded={commentingId === row.responseId}
                                  aria-label={
                                    (draft?.comment || "").trim()
                                      ? "Edit comment for the student"
                                      : "Add a comment for the student"
                                  }
                                  title="Comment for the student"
                                  onClick={() =>
                                    setCommentingId((prev) =>
                                      prev === row.responseId ? null : row.responseId,
                                    )
                                  }
                                >
                                  <MessageSquare size={15} aria-hidden="true" />
                                </button>
                              ) : null}
                              {canGrade && isScorable(row) && changed ? (
                                <button
                                  type="button"
                                  className="al-grade-mark-save"
                                  disabled={savingMarks || String(draft?.points ?? "").trim() === ""}
                                  onClick={() => void saveMarks([row.responseId])}
                                >
                                  Save
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </header>

                      <ResponseReview
                        question={row}
                        answer={row.answer}
                        verdict={row.isCorrect}
                        badges={gradingKindBadges(row)}
                      />

                      {!canGrade && row.type !== "directions" ? (
                        <p className="al-grade-settled">
                          {row.awaitingMarking
                            ? "Unmarked. You can read this answer but not score it."
                            : row.gradedAt
                              ? `Marked ${formatWhen(row.gradedAt)}`
                              : "Marked by the server"}
                          {row.teacherComment ? ` · “${row.teacherComment}”` : ""}
                        </p>
                      ) : null}
                    </article>
                  );
                })}

                {canGrade && dirtyIds.length > 1 ? (
                  <div className="al-grade-bulk">
                    <span>
                      {dirtyIds.length} answers changed and not saved
                    </span>
                    <button
                      type="button"
                      className="al-grade-bulk-save"
                      disabled={savingMarks}
                      onClick={() => void saveMarks(dirtyIds)}
                    >
                      {savingMarks ? "Saving…" : "Save all"}
                    </button>
                    <button
                      type="button"
                      className="al-grade-link"
                      onClick={() => setMarks(draftsFrom(paper.responses))}
                    >
                      Undo
                    </button>
                  </div>
                ) : null}
                  </section>
                ) : null}
              </main>

              {compactGrader ? (
                <div className="al-grade-docks">
                  <button
                    type="button"
                    className={`al-grade-dock-btn al-grade-dock-btn--attempts${gradeDock === "attempts" ? " is-open" : ""}`}
                    aria-expanded={gradeDock === "attempts"}
                    aria-controls="al-grade-rail"
                    onClick={() => setGradeDock((open) => (open === "attempts" ? null : "attempts"))}
                  >
                    <History size={16} aria-hidden="true" />
                    <span>Attempts</span>
                  </button>
                  {paper.responses.length ? (
                    <button
                      type="button"
                      className={`al-grade-dock-btn${gradeDock === "questions" ? " is-open" : ""}`}
                      aria-expanded={gradeDock === "questions"}
                      aria-controls="al-grade-rail"
                      onClick={() => setGradeDock((open) => (open === "questions" ? null : "questions"))}
                    >
                      <ListOrdered size={16} aria-hidden="true" />
                      <span>Questions</span>
                    </button>
                  ) : null}
                  <WorkThreadTrigger
                    open={threadOpen}
                    onClick={() => {
                      setThreadOpen((on) => !on);
                      setGradeDock(null);
                      setOverrideId(null);
                    }}
                  />
                  {detail?.canOverrideWork ? (
                    <OverrideTrigger open={overrideOpen} onClick={openPaperOverride} />
                  ) : null}
                </div>
              ) : null}
            </div>

            {threadOpen
              ? createPortal(
                  <div id="al-work-thread-panel" className="al-work-thread-panel" role="dialog" aria-label="Messages">
                    <div className="al-work-thread-panel-head">
                      <button
                        type="button"
                        className="al-grade-dock-close"
                        aria-label="Close messages"
                        onClick={() => setThreadOpen(false)}
                        style={{ display: "inline-flex", marginLeft: "auto" }}
                      >
                        <X size={15} aria-hidden="true" />
                      </button>
                    </div>
                    <WorkThread
                      data={workThread}
                      tab={threadTab}
                      audience={threadAudience}
                      channels
                      sending={threadSending}
                      onAudienceChange={(next) => setThreadAudience(next)}
                      onTabChange={(next) => {
                        setThreadTab(next);
                        if (next !== "all" && next !== paper.id) {
                          setPendingAttemptId(next);
                          setPaperLoading(true);
                          navigate(`${classPath}/work/${encodeURIComponent(next)}`);
                        }
                      }}
                      onJumpQuestion={(questionId) => {
                        const row = paper.responses.find((item) => item.questionId === questionId);
                        if (row) jumpToQuestion(row.responseId);
                      }}
                      onSend={async (body) => {
                        const progressId =
                          paper.progressId ||
                          classWork(detail).find(
                            (row) =>
                              row.activityId === paper.activityId && row.learnerUgpId === paper.learnerUgpId,
                          )?.progressId;
                        if (!courseId || !progressId) return;
                        setThreadSending(true);
                        try {
                          const envelope = await post<{ success: boolean; data: WorkThreadData }>(
                            `/api/teaching/classes/${encodeURIComponent(courseId)}/work/${encodeURIComponent(progressId)}/thread/messages`,
                            {
                              audience: threadAudience,
                              submissionId: messageSubmissionId(threadTab, workThread?.attempts),
                              body,
                            },
                          );
                          setWorkThread(envelope.data);
                        } catch (err) {
                          notice({
                            type: "error",
                            title: "Could not send",
                            subtext: errorMessage(err),
                            duration: 5000,
                          });
                        } finally {
                          setThreadSending(false);
                        }
                      }}
                    />
                  </div>,
                  document.body,
                )
              : null}
          </>
        ) : null}

        {commentingRow && commentingId
          ? createPortal(
              <div
                className="al-grade-comment-root"
                role="dialog"
                aria-modal="true"
                aria-labelledby="al-grade-comment-title"
              >
                <button
                  type="button"
                  className="al-grade-comment-scrim"
                  aria-label="Close comment"
                  tabIndex={-1}
                  onClick={() => setCommentingId(null)}
                />
                <div className="al-grade-comment-sheet">
                  <div className="al-grade-comment-inner">
                    <header className="al-grade-comment-head">
                      <div>
                        <h2 id="al-grade-comment-title">Comment for the student</h2>
                        {commentingIndex >= 0 ? (
                          <p className="al-grade-comment-sub">
                            Question {commentingIndex + 1}
                            {promptText(commentingRow.prompt)
                              ? ` · ${promptText(commentingRow.prompt)}`
                              : ""}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="al-grade-comment-close"
                        aria-label="Close comment"
                        onClick={() => setCommentingId(null)}
                      >
                        <X size={16} aria-hidden="true" />
                      </button>
                    </header>
                    <textarea
                      className="al-class-mark-comment al-grade-comment-field"
                      rows={4}
                      autoFocus
                      value={commentingDraft?.comment ?? ""}
                      onChange={(event) =>
                        editMark(commentingId, { comment: event.target.value })
                      }
                    />
                    <div className="al-grade-comment-foot">
                      {commentingChanged ? (
                        <button
                          type="button"
                          className="al-grade-mark-save"
                          disabled={
                            savingMarks || String(commentingDraft?.points ?? "").trim() === ""
                          }
                          onClick={() => void saveMarks([commentingId])}
                        >
                          Save
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>,
              document.body,
            )
          : null}

        <CountedAttemptHint open={attemptsHint} onClose={() => setAttemptsHint(false)} />
        <OverrideDialog
          row={resolveOverrideRow(overrideId, detail, paper)}
          learner={paper ? nameForUgp(detail, paper.learnerUgpId) : "Student"}
          grantingKey={grantingKey}
          onClose={() => setOverrideId(null)}
          onSetAttempts={(row, attempts) => void setAttemptCap(row, attempts)}
          onSetMessage={(row, studentMayMessage) => void setMessageOverride(row, studentMayMessage)}
          onSetLate={(row, allowLate) => void setLateOverride(row, allowLate)}
        />
        <CloseRetriesDialog
          open={closeRetriesOpen && Boolean(paper)}
          used={paper?.attemptsUsed ?? paper?.attempts.length ?? 0}
          max={paper?.maxAttempts ?? null}
          saving={grantingKey === (paper?.progressId || "paper")}
          onClose={() => setCloseRetriesOpen(false)}
          onStop={() => {
            if (!paper) return;
            const row = resolveOverrideRow(paper.progressId || "paper", detail, paper) || workRowFromPaper(paper);
            void setAttemptCap(row, closeAttemptCap(row.attemptsUsed)).then((ok) => {
              if (ok) setCloseRetriesOpen(false);
            });
          }}
        />
        <Dialog
          open={keyHint}
          title="How marking works"
          width="26rem"
          onClose={() => setKeyHint(false)}
        >
          <div className="al-hint-copy">
            <p>
              Keys on this page are the ones on the current activity in the
              course. Flip a panel to Key to see them.
            </p>
            {canGrade ? (
              <p>
                Every score can be changed, including the ones the server awarded.
                Leave a score blank and that answer stays unmarked — this try
                stays pending until the last one has a number. Only the gold star
                counts toward the grade.
              </p>
            ) : (
              <p>
                You can read this work. Awarding points needs Grade on the kit
                for this copy (enrollment for a teacher, Teach Without Enrollment
                for a supervisor).
              </p>
            )}
            <p>
              The colour on a card is the mark. The same colours on a matching
              pair or a choice are that row.
            </p>
            <ul className="al-hint-tones" aria-label="Mark colours">
              <li>
                <span className="al-grade-verdict al-grade-verdict--right">Correct</span>
                green — full marks
              </li>
              <li>
                <span className="al-grade-verdict al-grade-verdict--wrong">Not correct</span>
                red — wrong, including when attempt points were still awarded
              </li>
              <li>
                <span className="al-grade-verdict al-grade-verdict--part">Part</span>
                amber — some marks
              </li>
              <li>
                <span className="al-grade-verdict al-grade-verdict--wait">Unmarked</span>
                grey — no score yet
              </li>
            </ul>
          </div>
        </Dialog>
      </div>
    );
  }

  const announceTarget: AnnounceTarget | null = detail
    ? { courseId: detail.id, title: detail.title }
    : null;
  const overrideRow = resolveOverrideRow(overrideId, detail, paper);
  const overrideLearner = overrideRow
    ? detail?.learners.find((person) => person.uisUgpId === overrideRow.learnerUgpId)
    : null;

  return (
    <div className={`al-classes${classView === "gradebook" ? " al-classes--gradebook" : ""}`}>
      <LoadingOverlay isVisible={loading} message="Loading…" />
      <Link to="/teach" className="al-class-back">
        <ArrowLeft size={14} aria-hidden="true" />
        Teach
      </Link>

      {!loading && missing ? (
        <div className="al-classes-empty">
          <h2>Not found</h2>
          <p>This is not a course you instruct, or it is no longer on the school.</p>
        </div>
      ) : null}

      {detail ? (
        <>
          <header className="al-classes-header">
            <div className="al-classes-title-row">
              <h1>{detail.title}</h1>
              <button
                type="button"
                className="al-classes-help"
                aria-label="About this course"
                title="About this course"
                onClick={() => setAboutOpen(true)}
              >
                <CircleHelp size={16} aria-hidden="true" />
              </button>
            </div>
            <p className="al-classes-sub">
              {detail.schoolName || "School"} · {learnerLabel(detail.learnerCount)}
              {detail.description ? ` · ${detail.description}` : ""}
            </p>
            {canAnnounce ? (
              <div className="al-class-actions">
                <button
                  type="button"
                  className="al-classes-announce"
                  onClick={() => setAnnounceOpen(true)}
                >
                  <Megaphone size={13} aria-hidden="true" />
                  Announce
                </button>
              </div>
            ) : null}
          </header>

          <div className="al-class-view-tabs" role="tablist" aria-label="Class views">
            {detail.canViewWork ? (
              <>
                <button
                  type="button"
                  role="tab"
                  className={classView === "work" ? "is-on" : ""}
                  aria-selected={classView === "work"}
                  onClick={() => setClassView("work")}
                >
                  Work
                </button>
                <button
                  type="button"
                  role="tab"
                  className={classView === "gradebook" ? "is-on" : ""}
                  aria-selected={classView === "gradebook"}
                  onClick={() => setClassView("gradebook")}
                >
                  Gradebook
                </button>
              </>
            ) : null}
            <button
              type="button"
              role="tab"
              className={classView === "attendance" ? "is-on" : ""}
              aria-selected={classView === "attendance"}
              onClick={() => setClassView("attendance")}
            >
              Attendance
            </button>
          </div>

          {classView === "attendance" ? (
            <ClassAttendance courseId={detail.id} learners={detail.learners} />
          ) : classView === "gradebook" && detail.canViewWork ? (
            <section className="al-class-panel" id="gradebook">
              <ClassGradebook courseId={detail.id} learners={detail.learners} />
            </section>
          ) : (
          <section className="al-class-panel" id="work">
            <div className="al-class-panel-head">
              <h2>
                <ClipboardList size={15} aria-hidden="true" />
                Work
              </h2>
              {detail.canViewWork ? (
                <div className="al-class-panel-tools">
                  <div className="al-class-filter-row">
                    <button
                      type="button"
                      className={`al-class-chip-toggle${workFilter === "grade" ? " is-on" : ""}`}
                      aria-pressed={workFilter === "grade"}
                      onClick={() => setWorkFilter("grade")}
                    >
                      Grading required
                      <span className="al-class-chip-toggle-count">{toGradeWork.length}</span>
                    </button>
                    <button
                      type="button"
                      className={`al-class-chip-toggle${workFilter === "path" ? " is-on" : ""}`}
                      aria-pressed={workFilter === "path"}
                      onClick={() => setWorkFilter("path")}
                    >
                      Full Path
                      <span className="al-class-chip-toggle-count">{scopedWork.length}</span>
                    </button>
                    {detail.learners.length > 0 ? (
                      <button
                        type="button"
                        className={`al-classes-help${studentFilter ? " is-on" : ""}`}
                        aria-label={
                          studentFilter
                            ? `Filtered to ${nameForUgp(detail, studentFilter)}. Change student filter`
                            : "Filter by student"
                        }
                        title={
                          studentFilter
                            ? nameForUgp(detail, studentFilter)
                            : "Filter by student"
                        }
                        aria-pressed={Boolean(studentFilter)}
                        onClick={() => setStudentPickerOpen(true)}
                      >
                        <User size={16} aria-hidden="true" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="al-classes-help"
                      aria-label="Which attempt counts — the gold star"
                      title="Which attempt counts — the gold star"
                      onClick={() => setAttemptsHint(true)}
                    >
                      <CircleHelp size={16} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            {!detail.canViewWork ? (
              <p className="al-class-empty">
                You can teach this course, but viewing student work needs Grade
                on your enrollment kit, or view/grade on Teach Without Enrollment.
              </p>
            ) : visibleWork.length === 0 ? (
              <p className="al-class-empty">
                {allWork.length === 0
                  ? "Nothing started yet."
                  : workFilter === "grade"
                    ? "Nothing waiting for a mark."
                    : studentFilter
                      ? "This student has not started anything yet."
                      : "Nothing to show."}
              </p>
            ) : (
              <ul className="al-class-work">
                {visibleWork.map((row) => {
                  const learner = detail.learners.find((person) => person.uisUgpId === row.learnerUgpId);
                  const draftOnly = isDraftRow(row);
                  const discussion = row.activityType === "discussion";
                  const href = discussion
                    ? `${classPath}/discussions/${encodeURIComponent(row.activityId)}?student=${encodeURIComponent(row.learnerUgpId)}`
                    : row.submissionId && !draftOnly
                      ? `${classPath}/work/${encodeURIComponent(row.submissionId)}`
                      : `${classPath}/progress/${encodeURIComponent(row.progressId)}`;
                  const statusLabel = discussion
                    ? "Posted"
                    : row.submissionId
                      ? formatAttemptsUsed(row.attemptsUsed, row.maxAttempts)
                      : row.progressStatus === "completed"
                        ? "Completed"
                        : "Opened";
                  const body = (
                    <>
                      <span className="al-class-work-title">
                        {row.activityTitle || "Activity"}
                        {draftOnly ? <span className="al-class-chip">Draft</span> : null}
                        {row.hasNewerPending ? (
                          <span
                            className={`al-class-chip al-class-chip--later${row.newerTryAhead ? " al-class-chip--caution" : ""}`}
                            title={
                              row.newerTryAhead
                                ? "Already outscoring the counted try — mark it so this one can count"
                                : undefined
                            }
                            aria-label={
                              row.newerTryAhead
                                ? "Newer try, already outscoring the counted attempt"
                                : undefined
                            }
                          >
                            {row.newerTryAhead ? <AlertTriangle size={11} strokeWidth={2.4} aria-hidden="true" /> : null}
                            Newer try
                          </span>
                        ) : row.needsMark ? (
                          <span className="al-class-chip al-class-chip--wait">Needs a mark</span>
                        ) : null}
                        {row.overrideAttempts != null ? (
                          <span className="al-class-chip">Limit {row.overrideAttempts}</span>
                        ) : null}
                        {row.overrideStudentMayMessage === false ? (
                          <span className="al-class-chip">Chat off</span>
                        ) : null}
                        {row.overrideAllowLate ? (
                          <span className="al-class-chip">Late allowed</span>
                        ) : null}
                      </span>
                      <span className="al-class-work-meta">
                        {learner ? learnerName(learner) : "Student"} · {statusLabel}
                        {row.pointsPossible != null ? ` · ${formatScore(row)}` : ""}
                        {row.lastAt || row.submittedAt
                          ? ` · ${formatWhen(row.lastAt || row.submittedAt)}`
                          : ""}
                      </span>
                    </>
                  );
                  return (
                    <li key={row.progressId} className="al-class-work-item">
                      {href ? (
                        <Link to={href} className="al-class-work-row">
                          {body}
                        </Link>
                      ) : (
                        <div className="al-class-work-row al-class-work-row--static">{body}</div>
                      )}
                      {detail.canOverrideWork ? (
                        <button
                          type="button"
                          className="al-classes-help"
                          aria-label="Special overrides"
                          title="Special overrides"
                          onClick={() => setOverrideId(row.progressId)}
                        >
                          <Settings size={15} aria-hidden="true" />
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          )}
        </>
      ) : null}

      <CourseAnnounceDialog
        target={announceOpen ? announceTarget : null}
        onClose={() => setAnnounceOpen(false)}
      />
      <CountedAttemptHint open={attemptsHint} onClose={() => setAttemptsHint(false)} />
      <OverrideDialog
        row={overrideRow}
        learner={overrideLearner ? learnerName(overrideLearner) : "Student"}
        grantingKey={grantingKey}
        onClose={() => setOverrideId(null)}
        onSetAttempts={(row, attempts) => void setAttemptCap(row, attempts)}
        onSetMessage={(row, studentMayMessage) => void setMessageOverride(row, studentMayMessage)}
        onSetLate={(row, allowLate) => void setLateOverride(row, allowLate)}
      />
      <StudentPickerDialog
        open={studentPickerOpen}
        learners={detail?.learners || []}
        selectedUgp={studentFilter}
        onClose={() => setStudentPickerOpen(false)}
        onPick={(ugpId) => {
          setStudentFilter(ugpId);
          setStudentPickerOpen(false);
        }}
      />
      <Dialog open={aboutOpen} title="This course" width="26rem" onClose={() => setAboutOpen(false)}>
        <div className="al-hint-copy">
          <p>
            Propose and Edit stay on the course card and in Courses, when the
            course locks and your enrollment kit (or Edit Courses) allow it.
            This page is the class: Work and the gradebook. Extra attempts
            need Override. If you are enrolled and also a supervisor, the
            enrollment kit wins.
          </p>
        </div>
      </Dialog>

    </div>
  );
}
